import { useState, useEffect, useRef } from "react";
import {
  LayoutDashboard, Wrench, Ticket, Map, Package, Calendar,
  Settings, Users, BarChart3, Bell, Search, Moon, Sun,
  Menu, X, ChevronRight, Plus, LogOut, Truck, Clock,
  TrendingUp, AlertCircle, CheckCircle2, CircleDot
} from "lucide-react";

const BASE = "https://support.gs-group.fr/api";
const h = () => ({ "Content-Type":"application/json", Authorization:"Bearer "+localStorage.getItem("nexo_token") });

// ===== DARK MODE AUTO (heure + système) =====
function useAutoTheme() {
  const getAuto = () => {
    const hour = new Date().getHours();
    const sysDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    return sysDark || hour >= 19 || hour < 7;
  };

  const saved = localStorage.getItem("gs_dark");
  const [dark, setDark] = useState(saved !== null ? saved === "true" : getAuto());

  useEffect(() => {
    // Appliquer la classe
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  useEffect(() => {
    // Écouter le système
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const fn = () => {
      if (localStorage.getItem("gs_dark") === null) setDark(getAuto());
    };
    mq.addEventListener('change', fn);
    // Check toutes les minutes pour l'heure
    const t = setInterval(() => {
      if (localStorage.getItem("gs_dark") === null) setDark(getAuto());
    }, 60000);
    return () => { mq.removeEventListener('change', fn); clearInterval(t); };
  }, []);

  const toggle = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem("gs_dark", String(next));
  };

  return { dark, toggle };
}

// ===== NAV ITEMS =====
const NAV = [
  { id:"dashboard", label:"Dashboard",      icon:LayoutDashboard },
  { id:"interventions", label:"Interventions", icon:Wrench },
  { id:"sav",     label:"Tickets SAV",    icon:Ticket },
  { id:"carte",   label:"Carte",          icon:Map },
  { id:"stock",   label:"Stock",          icon:Package },
  { id:"planning", label:"Planning",      icon:Calendar },
  { id:"stats",   label:"Statistiques",  icon:BarChart3 },
  { id:"vehicules", label:"Véhicules",   icon:Truck },
];

const NAV_BOTTOM = [
  { id:"settings", label:"Paramètres", icon:Settings },
  { id:"users",   label:"Utilisateurs", icon:Users },
];

// ===== STATUTS =====
const STAT_COLOR = {
  en_cours:"text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400",
  "in-progress":"text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400",
  planifiee:"text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400",
  pending:"text-amber-600 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-400",
  terminee:"text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400",
  completed:"text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400",
  annulee:"text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400",
  brouillon:"text-violet-600 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-400",
};
const STAT_LABEL = {en_cours:"En cours","in-progress":"En cours",planifiee:"Planifié",pending:"Planifié",terminee:"Terminé",completed:"Terminé",annulee:"Annulé",brouillon:"Brouillon"};

export default function AppShell({ user, interventions=[], tickets=[], stock=[], techniciens=[], clients=[], view, setView, children, onLogout, onNewInt, onNewSAV, selInt, setSelInt }) {
  const { dark, toggle } = useAutoTheme();
  const [sideOpen, setSideOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [q, setQ] = useState("");
  const searchRef = useRef(null);

  // Ctrl+K
  useEffect(() => {
    const fn = e => { if ((e.metaKey||e.ctrlKey) && e.key==="k") { e.preventDefault(); setSearchOpen(v=>!v); } };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, []);

  // Fermer sidebar sur mobile au changement de vue
  useEffect(() => { setSideOpen(false); }, [view]);

  // Polling notifs
  useEffect(() => {
    const fn = async () => {
      try {
        const d = await fetch(`${BASE}/notifications/unread`, {headers:h()}).then(r=>r.json());
        if (d.count !== undefined) setUnread(d.count);
      } catch(e) {}
    };
    fn();
    const t = setInterval(fn, 30000);
    return () => clearInterval(t);
  }, []);

  const filtered = q.length > 1 ? {
    ints: interventions.filter(i => (i.titre+i.client_nom+i.client_ville).toLowerCase().includes(q.toLowerCase())).slice(0,4),
    sav: tickets.filter(t => (t.objet+t.ville).toLowerCase().includes(q.toLowerCase())).slice(0,3),
  } : null;

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 font-sans overflow-hidden">

      {/* === OVERLAY MOBILE === */}
      {sideOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setSideOpen(false)}/>
      )}

      {/* === SIDEBAR === */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 flex flex-col
        bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800
        transition-transform duration-300 ease-in-out
        w-64
        md:relative md:translate-x-0 md:flex
        ${sideOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-gray-100 dark:border-gray-800">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200 dark:shadow-indigo-900/40">
            <span className="text-white font-black text-sm">GS</span>
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900 dark:text-white leading-tight">GS-GROUP</div>
            <div className="text-[10px] text-gray-400 font-medium">Support Technique</div>
          </div>
          <button className="ml-auto md:hidden text-gray-400 hover:text-gray-600" onClick={() => setSideOpen(false)}>
            <X size={18}/>
          </button>
        </div>

        {/* Nav principale */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {NAV.map(item => {
            const Icon = item.icon;
            const active = view === item.id;
            return (
              <button key={item.id} onClick={() => setView(item.id)}
                className={`sidebar-item w-full text-left ${active ? "active" : ""}`}>
                <Icon size={17} strokeWidth={active ? 2.5 : 2}/>
                <span className="flex-1">{item.label}</span>
                {item.id==="sav" && tickets.filter(t=>["ouvert","open"].includes(t.statut)).length > 0 && (
                  <span className="badge bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400">
                    {tickets.filter(t=>["ouvert","open"].includes(t.statut)).length}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Bas sidebar */}
        <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-800 space-y-0.5">
          {NAV_BOTTOM.map(item => {
            const Icon = item.icon;
            return (
              <button key={item.id} onClick={() => setView(item.id)}
                className={`sidebar-item w-full text-left ${view===item.id ? "active" : ""}`}>
                <Icon size={17}/><span>{item.label}</span>
              </button>
            );
          })}

          {/* User */}
          <div className="flex items-center gap-3 px-3 py-2.5 mt-1">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {(user?.prenom?.[0]||"")+(user?.nom?.[0]||"")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{user?.prenom} {user?.nom}</div>
              <div className="text-[10px] text-gray-400 capitalize">{user?.role}</div>
            </div>
            <button onClick={onLogout} className="text-gray-400 hover:text-red-500 transition-colors" title="Déconnexion">
              <LogOut size={15}/>
            </button>
          </div>
        </div>
      </aside>

      {/* === MAIN === */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">

        {/* TOPBAR */}
        <header className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-5 h-14 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">

          {/* Burger mobile */}
          <button className="md:hidden text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            onClick={() => setSideOpen(true)}>
            <Menu size={20}/>
          </button>

          {/* Titre page mobile */}
          <div className="md:hidden text-sm font-bold text-gray-800 dark:text-gray-200 flex-1">
            {NAV.find(n=>n.id===view)?.label || NAV_BOTTOM.find(n=>n.id===view)?.label || "Dashboard"}
          </div>

          {/* Recherche desktop */}
          <div className="hidden md:flex flex-1 max-w-md">
            <button onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2.5 w-full px-3.5 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-400 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors">
              <Search size={15}/>
              <span className="flex-1 text-left">Rechercher...</span>
              <kbd className="text-[10px] bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-1.5 py-0.5 text-gray-400">⌘K</kbd>
            </button>
          </div>

          <div className="flex items-center gap-1.5 ml-auto">
            {/* Recherche mobile */}
            <button className="md:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setSearchOpen(true)}>
              <Search size={18}/>
            </button>

            {/* Dark mode */}
            <button onClick={toggle}
              className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              {dark ? <Sun size={18}/> : <Moon size={18}/>}
            </button>

            {/* Notifs */}
            <button className="relative p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={() => setNotifOpen(v=>!v)}>
              <Bell size={18}/>
              {unread > 0 && (
                <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-black rounded-full flex items-center justify-center">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>

            {/* CTA desktop */}
            <div className="hidden sm:flex items-center gap-2 ml-1">
              <button onClick={onNewInt} className="btn-primary flex items-center gap-1.5">
                <Plus size={15}/> Intervention
              </button>
            </div>
          </div>
        </header>

        {/* CONTENU */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-5 lg:p-6 pb-20 md:pb-6">
          {children}
        </main>

        {/* NAV MOBILE BAS */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800 flex justify-around items-center px-2 pt-2 pb-safe z-30"
          style={{paddingBottom:"max(12px,env(safe-area-inset-bottom))"}}>
          {NAV.slice(0,5).map(item => {
            const Icon = item.icon;
            const active = view === item.id;
            return (
              <button key={item.id} onClick={() => setView(item.id)}
                className={`flex flex-col items-center gap-1 px-2 py-1 rounded-xl min-w-[52px] transition-all relative ${active ? "text-indigo-600 dark:text-indigo-400" : "text-gray-400"}`}>
                {item.id==="sav" && tickets.filter(t=>["ouvert","open"].includes(t.statut)).length > 0 && (
                  <span className="absolute top-0 right-2 w-2 h-2 bg-red-500 rounded-full"/>
                )}
                <Icon size={22} strokeWidth={active ? 2.5 : 1.8}/>
                <span className={`text-[10px] ${active ? "font-bold" : "font-normal"}`}>{item.label.split(" ")[0]}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* === SEARCH MODAL === */}
      {searchOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center pt-16 px-4 animate-fade-in"
          onClick={e => e.target===e.currentTarget && setSearchOpen(false)}>
          <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl overflow-hidden animate-slide-up">
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 dark:border-gray-800">
              <Search size={18} className="text-gray-400 flex-shrink-0"/>
              <input autoFocus value={q} onChange={e=>setQ(e.target.value)}
                placeholder="Rechercher intervention, SAV, client..."
                className="flex-1 bg-transparent outline-none text-gray-900 dark:text-gray-100 text-sm placeholder-gray-400"
                onKeyDown={e=>e.key==="Escape"&&setSearchOpen(false)}/>
              <button onClick={()=>setSearchOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={18}/>
              </button>
            </div>

            {filtered ? (
              <div className="max-h-96 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                {filtered.ints.map(i => (
                  <button key={i.id} onClick={()=>{setSelInt(i);setView("interventions");setSearchOpen(false);setQ("");}}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center flex-shrink-0">
                      <Wrench size={15} className="text-indigo-600 dark:text-indigo-400"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{i.titre}</div>
                      <div className="text-xs text-gray-400">{i.client_nom} · {i.client_ville}</div>
                    </div>
                    <span className={`badge text-[10px] ${STAT_COLOR[i.statut]||""}`}>{STAT_LABEL[i.statut]||i.statut}</span>
                  </button>
                ))}
                {filtered.sav.map(t => (
                  <button key={t.id} onClick={()=>{setView("sav");setSearchOpen(false);setQ("");}}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors">
                    <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                      <Ticket size={15} className="text-red-500"/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{t.objet}</div>
                      <div className="text-xs text-gray-400">{t.ville} · {t.priorite}</div>
                    </div>
                  </button>
                ))}
                {filtered.ints.length===0 && filtered.sav.length===0 && (
                  <div className="py-10 text-center text-gray-400 text-sm">Aucun résultat</div>
                )}
              </div>
            ) : (
              <div className="px-4 py-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Accès rapide</p>
                <div className="flex flex-wrap gap-2">
                  {NAV.map(n => {
                    const Icon = n.icon;
                    return (
                      <button key={n.id} onClick={()=>{setView(n.id);setSearchOpen(false);}}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300 hover:border-indigo-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                        <Icon size={13}/>{n.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex gap-4 text-[10px] text-gray-400">
              <span>↵ Ouvrir</span><span>Échap Fermer</span><span>⌘K Recherche</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
